// ── Incremental map-object diff ──
// Extracted verbatim from MapCanvas.tsx. Handles the "only removals" / "only
// additions" / "only position-size changes" cases so the map does NOT do a full
// FULL RE-RENDER for every mapObjects snapshot change. Stays a useLayoutEffect
// (it must run before the main PIXI render effect so lastMapObjectsRenderKeyRef
// is already set) and is called from the same position in MapCanvas, so its
// order relative to the surrounding effects is unchanged.
//
// The body is byte-identical to the original; every closure dependency is a
// parameter under its ORIGINAL name. `getMapObjectRenderSignaturePiece` and
// `createPlotIcon` are imported straight from their own modules (no back-import
// from MapCanvas); `ISLAND_TYPE_TO_IMAGE` and `mapLoadedTexturePaths` are
// passed in, exactly as useMapObjectsRender already receives them.
import { useLayoutEffect, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapObject } from '../../models/mapObject';
import { createPlotIcon } from './render/mapMarkers';
import { ensurePlotMarkerTexture } from './mapScene';
import { getMapObjectRenderSignaturePiece } from './useMapData';
import type { MarkerFactory } from './controller/markers/MarkerFactory';
import type { MapMarkerContainer } from './render/mapPixiExtensions';

type MarkerClickHandler = (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;

export interface UseIncrementalMapObjectsParams {
  /** Effect deps — by value, re-read every render. */
  mapObjects: MapObject[];
  mapObjectsRenderKey: string;
  isPixiAppReady: boolean;
  markersReady: boolean;
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  /** Module-level handler refs kept alive across remounts — by reference. */
  handlePlotClickRef: { current: MarkerClickHandler | null };
  handleMapObjectClickRef: { current: MarkerClickHandler | null };
  /** Marker factory facade — by reference; `.current` is read after `await`. */
  markerFactoryRef: MutableRefObject<MarkerFactory | null>;
  /** Mirrors the render key into the scene singleton. */
  setLastRenderKey: (key: string | null) => void;
  ISLAND_TYPE_TO_IMAGE: Record<string, number>;
  mapLoadedTexturePaths: Set<string>;
}

export function useIncrementalMapObjects({
  mapObjects,
  mapObjectsRenderKey,
  isPixiAppReady,
  markersReady,
  sceneRef,
  handlePlotClickRef,
  handleMapObjectClickRef,
  markerFactoryRef,
  setLastRenderKey,
  ISLAND_TYPE_TO_IMAGE,
  mapLoadedTexturePaths,
}: UseIncrementalMapObjectsParams): void {
  // Лише видалення або лише нові id у списку — без повного FULL RE-RENDER усієї мапи.
  // useLayoutEffect: виконується до useEffect з основного PIXI-блоку, щоб встигнути виставити lastMapObjectsRenderKeyRef.
  useLayoutEffect(() => {
    if (!isPixiAppReady) return;

    const prev = sceneRef.current.data.prevMapObjectsSnapshotForIncremental;
    const next = mapObjects;

    const prevById = new Map(prev.map(o => [o.id, o]));
    const nextById = new Map(next.map(o => [o.id, o]));

    const removedIds: number[] = [];
    for (const id of prevById.keys()) {
      if (!nextById.has(id)) removedIds.push(id);
    }

    let addedCount = 0;
    for (const id of nextById.keys()) {
      if (!prevById.has(id)) addedCount++;
    }

    let hasModification = false;
    let onlyPositionSizeChanges = false;
    const modifiedIds: number[] = [];
    for (const id of nextById.keys()) {
      const p = prevById.get(id);
      const n = nextById.get(id)!;
      if (!p) continue;
      if (getMapObjectRenderSignaturePiece(p) !== getMapObjectRenderSignaturePiece(n)) {
        hasModification = true;
        modifiedIds.push(id);
      }
    }
    // Detect position/size-only changes: same id/type/owner, only x/y/display_width/display_height differ.
    // Admin drag already moved the sprite, so skip full rebuild — just update the render key.
    if (hasModification && removedIds.length === 0 && addedCount === 0) {
      onlyPositionSizeChanges = modifiedIds.every(id => {
        const p = prevById.get(id)!;
        const n = nextById.get(id)!;
        return p.type === n.type && p.owner_id === n.owner_id
          && (p.type !== 'house' || (p.mirror_direction ?? 'left') === (n.mirror_direction ?? 'left'));
      });
    }

    const onlyRemovals = addedCount === 0 && removedIds.length > 0 && !hasModification;
    const onlyAdditions =
      removedIds.length === 0 && addedCount > 0 && !hasModification && prev.length > 0;

    if (onlyPositionSizeChanges && markersReady) {
      // Admin moved/resized objects — sprites are already at correct position, skip full rebuild
      setLastRenderKey(mapObjectsRenderKey);
    } else if (onlyRemovals && markersReady) {
      const layer = sceneRef.current.layers.mapObjects;
      const islandLayer = sceneRef.current.layers.island;

      for (const id of removedIds) {
        const removedObj = prevById.get(id);
        if (!removedObj) continue;

        sceneRef.current.admin.objectModes.delete(id);

        const sel = sceneRef.current.admin.selectedMarker;
        if (sel?.objectId === id) {
          sceneRef.current.admin.selectedMarker = null;
        }

        if (removedObj.type === 'plot') {
          const m = sceneRef.current.markers.plots.get(id);
          if (m && layer && !m.destroyed) {
            layer.removeChild(m);
            m.destroy();
          }
          sceneRef.current.markers.plots.delete(id);
        } else if (removedObj.type === 'house') {
          const m = sceneRef.current.markers.realEstate.get(id);
          if (m && layer && !m.destroyed) {
            layer.removeChild(m);
            m.destroy();
          }
          sceneRef.current.markers.realEstate.delete(id);
          const sale = sceneRef.current.markers.sales.get(id);
          if (sale) {
            if (sale.parent) sale.parent.removeChild(sale);
            if (!sale.destroyed) sale.destroy();
            sceneRef.current.markers.sales.delete(id);
          }
          const avatar = sceneRef.current.markers.avatars.get(id);
          if (avatar) {
            if (avatar.parent) avatar.parent.removeChild(avatar);
            if (!avatar.destroyed) avatar.destroy();
            sceneRef.current.markers.avatars.delete(id);
          }
        } else if (removedObj.type.startsWith('island_')) {
          const m = sceneRef.current.markers.islands.get(id);
          if (m && islandLayer && !m.destroyed) {
            islandLayer.removeChild(m);
            m.destroy();
          }
          sceneRef.current.markers.islands.delete(id);
        } else {
          const m = sceneRef.current.markers.mapObjects.get(id);
          if (m && layer && !m.destroyed) {
            layer.removeChild(m);
            m.destroy();
          }
          sceneRef.current.markers.mapObjects.delete(id);
        }

        const questMarker = sceneRef.current.markers.quests.get(id);
        if (questMarker && layer && !questMarker.destroyed) {
          layer.removeChild(questMarker);
          questMarker.destroy();
          sceneRef.current.markers.quests.delete(id);
        }
      }

      setLastRenderKey(mapObjectsRenderKey);
    } else if (onlyAdditions && markersReady) {
      setLastRenderKey(mapObjectsRenderKey);

      const addedObjects = next.filter(o => !prevById.has(o.id));
      const CHUNK_SIZE = 8;
      const yieldFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));

      void (async () => {
        const layer = sceneRef.current.layers.mapObjects;
        if (!layer) return;

        for (let i = 0; i < addedObjects.length; i++) {
          if (!sceneRef.current.layers.mapObjects || !sceneRef.current.pixi.app) return;
          if (i > 0 && i % CHUNK_SIZE === 0) await yieldFrame();

          const obj = addedObjects[i];
          try {
            if (obj.type === 'plot') {
              // Must not build a plot marker without its texture — see createPlotIcon.
              const plotMarkerTexture = await ensurePlotMarkerTexture();
              if (!sceneRef.current.layers.mapObjects) return;
              const marker = createPlotIcon(obj, plotMarkerTexture);
              marker.visible = sceneRef.current.flags.showPlots;
              marker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
                handlePlotClickRef.current?.(obj, marker, event);
              });
              layer.addChild(marker);
              sceneRef.current.markers.plots.set(obj.id, marker);
            } else if (obj.type === 'house') {
              const marker = await markerFactoryRef.current!.createRealEstateMarker(obj);
              if (!marker || !sceneRef.current.layers.mapObjects) continue;
              marker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
                handlePlotClickRef.current?.(obj, marker, event);
              });
              layer.addChild(marker);
              sceneRef.current.markers.realEstate.set(obj.id, marker);
            } else if (obj.type.startsWith('island_')) {
              if (!sceneRef.current.layers.island) continue;
              const imageIndex = ISLAND_TYPE_TO_IMAGE[obj.type] ?? 0;
              const islandPath = `/game-assets/islands/${imageIndex}.webp`;
              const texture = await PIXI.Assets.load(islandPath);
              mapLoadedTexturePaths.add(islandPath);
              if (texture.source) {
                texture.source.autoGenerateMipmaps = true;
                texture.source.scaleMode = 'linear';
              }
              if (!sceneRef.current.layers.island) return;
              const container = new PIXI.Container();
              const sprite = new PIXI.Sprite(texture);
              sprite.anchor.set(0.5);
              if (obj.display_width && obj.display_height) {
                sprite.width = obj.display_width;
                sprite.height = obj.display_height;
              }
              if (obj.data?.mirror_h) {
                sprite.scale.x = -Math.abs(sprite.scale.x);
              }
              if (obj.data?.rotate_180) {
                sprite.scale.x = -Math.abs(sprite.scale.x);
              }
              container.addChild(sprite);
              container.position.set(obj.x, obj.y);
              container.cullable = true;
              (container as MapMarkerContainer).mapObjectData = obj;
              container.eventMode = 'none';
              sceneRef.current.layers.island.addChild(container);
              sceneRef.current.markers.islands.set(obj.id, container);
            } else {
              const marker = await markerFactoryRef.current!.createMapObjectMarker(obj);
              if (!marker || !sceneRef.current.layers.mapObjects) continue;
              marker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
                handleMapObjectClickRef.current?.(obj, marker, event);
              });
              layer.addChild(marker);
              sceneRef.current.markers.mapObjects.set(obj.id, marker);
            }
          } catch (error) {
            console.error(`[MapCanvas] Incremental add marker failed for ${obj.type} id ${obj.id}:`, error);
          }
        }
      })();
    }

    sceneRef.current.data.prevMapObjectsSnapshotForIncremental = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- тригер лише зміна snapshot мапи; create* змінюються щорендеру
  }, [mapObjects, mapObjectsRenderKey, isPixiAppReady, markersReady]);
}
